import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  radiosVerificadas,
  categoriasVerificacion,
  beneficiosVerificacion,
  ubicacionesInsignia,
  reglasRadiosVerificadas,
  statsRadiosVerificadas,
  getRadiosPorCategoria,
  getRadiosEnTendencia,
  getRadiosEnVivo,
  formatearNumero,
  type CategoriaRadioVerificada,
  type RadioVerificada,
} from '@/mocks/radiosVerificadas';

function CategoriaBadge({ categoria }: { categoria: CategoriaRadioVerificada }) {
  const c = categoriasVerificacion.find(cat => cat.id === categoria);
  if (!c) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bg} ${c.color} border ${c.borde}`}>
      <i className={`${c.icono} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

function RadioVerificadaCard({ radio }: { radio: RadioVerificada }) {
  const [showDetail, setShowDetail] = useState(false);
  const cat = categoriasVerificacion.find(c => c.id === radio.categoriaVerificacion);

  return (
    <>
      <div
        onClick={() => setShowDetail(true)}
        className="group rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-amber-300/60 transition-all duration-300 cursor-pointer"
      >
        <div className="relative h-40 overflow-hidden">
          <img
            src={radio.imagenUrl}
            alt={radio.nombre}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50/95 via-background-50/30 to-transparent"></div>

          <div className="absolute top-3 left-3 flex items-center gap-2">
            {radio.estaEnVivo && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[9px] font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                EN VIVO
              </span>
            )}
            <CategoriaBadge categoria={radio.categoriaVerificacion} />
          </div>

          <div className="absolute top-3 right-3">
            <img src={radio.logoUrl} alt="" className="w-11 h-11 rounded-xl object-cover border-2 border-white/80 shadow-sm" />
          </div>

          {radio.enTendencia && (
            <div className="absolute bottom-3 left-3">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/90 text-white text-[9px] font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                TENDENCIA
              </span>
            </div>
          )}
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className="text-sm font-bold text-foreground-950 line-clamp-1">{radio.nombre}</h4>
            <span className="shrink-0 text-[10px] font-bold text-foreground-400">#{radio.posicionRanking}</span>
          </div>
          <p className="text-[10px] text-foreground-500 mb-2">{radio.frecuencia} · {radio.ciudad}, {radio.pais}</p>
          <p className="text-[10px] text-foreground-600 line-clamp-2 mb-3 leading-relaxed">{radio.descripcion}</p>

          <div className="flex items-center justify-between text-[9px]">
            <div className="flex items-center gap-3 text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-headphone-line text-[8px]"></i>
                {formatearNumero(radio.oyentes)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-group-line text-[8px]"></i>
                {formatearNumero(radio.seguidores)}
              </span>
            </div>
            <span className="text-amber-600 font-medium flex items-center gap-0.5">
              <i className="ri-star-fill text-[8px]"></i>
              {radio.rating}
            </span>
          </div>

          <div className="flex flex-wrap gap-1 mt-3">
            {radio.tags.slice(0, 4).map(tag => (
              <span key={tag} className="px-1.5 py-0.5 rounded-md bg-background-100 text-[8px] text-foreground-400 border border-background-200/50">
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-3 pt-3 border-t border-background-200/50">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-foreground-500">Locutor:</span>
              <span className="text-[10px] font-medium text-foreground-700">{radio.locutorPrincipal}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] text-foreground-500">Verificado:</span>
              <span className="text-[10px] text-foreground-600">{radio.fechaVerificacion} por {radio.otorgadoPor}</span>
            </div>
          </div>
        </div>
      </div>

      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground-950/60 backdrop-blur-sm" onClick={() => setShowDetail(false)}>
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-background-50 shadow-xl" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowDetail(false)}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-background-50/80 backdrop-blur-sm border border-background-200 flex items-center justify-center hover:bg-background-100 transition-all cursor-pointer"
            >
              <i className="ri-close-line text-foreground-700"></i>
            </button>

            <div className="relative h-52 overflow-hidden rounded-t-3xl">
              <img src={radio.imagenUrl} alt={radio.nombre} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent"></div>
              <div className="absolute bottom-4 left-6 flex items-center gap-3">
                <img src={radio.logoUrl} alt="" className="w-14 h-14 rounded-xl object-cover border-2 border-white shadow-lg" />
                <div>
                  <h3 className="text-xl font-bold text-foreground-950">{radio.nombre}</h3>
                  <p className="text-xs text-foreground-500">{radio.frecuencia}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-xl bg-background-100 text-center">
                  <p className="text-lg font-bold text-foreground-950">{formatearNumero(radio.oyentes)}</p>
                  <p className="text-[9px] text-foreground-400">Oyentes</p>
                </div>
                <div className="p-3 rounded-xl bg-background-100 text-center">
                  <p className="text-lg font-bold text-foreground-950">{formatearNumero(radio.seguidores)}</p>
                  <p className="text-[9px] text-foreground-400">Seguidores</p>
                </div>
                <div className="p-3 rounded-xl bg-background-100 text-center">
                  <p className="text-lg font-bold text-foreground-950">#{radio.posicionRanking}</p>
                  <p className="text-[9px] text-foreground-400">Ranking</p>
                </div>
                <div className="p-3 rounded-xl bg-background-100 text-center">
                  <p className="text-lg font-bold text-foreground-950">{radio.rating}</p>
                  <p className="text-[9px] text-foreground-400">Rating</p>
                </div>
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed">{radio.descripcion}</p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px]">
                <div>
                  <span className="text-foreground-400">Categoría</span>
                  <p className="font-medium text-foreground-700">{radio.categoriaRadio}</p>
                </div>
                <div>
                  <span className="text-foreground-400">Ubicación</span>
                  <p className="font-medium text-foreground-700">{radio.ciudad}, {radio.pais}</p>
                </div>
                <div>
                  <span className="text-foreground-400">Locutor</span>
                  <p className="font-medium text-foreground-700">{radio.locutorPrincipal}</p>
                </div>
                <div>
                  <span className="text-foreground-400">Verificación</span>
                  <p className="font-medium text-foreground-700">{radio.fechaVerificacion}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <CategoriaBadge categoria={radio.categoriaVerificacion} />
                <span className="text-[10px] text-foreground-500">— Otorgado por {radio.otorgadoPor} · Insignia {radio.numeroInsignia}</span>
              </div>

              <div>
                <h4 className="text-xs font-bold text-foreground-950 mb-2">Beneficios Activos</h4>
                <div className="flex flex-wrap gap-1.5">
                  {radio.beneficiosActivos.map(b => (
                    <span key={b} className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[9px] font-medium border border-emerald-200">
                      {b}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-bold text-foreground-950 mb-2">Tags</h4>
                <div className="flex flex-wrap gap-1.5">
                  {radio.tags.map(tag => (
                    <span key={tag} className="px-2 py-0.5 rounded-md bg-background-100 text-[9px] text-foreground-500 border border-background-200/50">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function RadiosVerificadasPage() {
  const [view, setView] = useState<'radios' | 'categorias' | 'beneficios' | 'reglas'>('radios');
  const [categoriaFilter, setCategoriaFilter] = useState<CategoriaRadioVerificada | 'todas'>('todas');
  const [sortBy, setSortBy] = useState<'ranking' | 'oyentes' | 'rating' | 'tendencia'>('ranking');

  const filtered = categoriaFilter === 'todas' ? [...radiosVerificadas] : getRadiosPorCategoria(categoriaFilter);
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'ranking') return a.posicionRanking - b.posicionRanking;
    if (sortBy === 'oyentes') return b.oyentes - a.oyentes;
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'tendencia') return (b.enTendencia ? 1 : 0) - (a.enTendencia ? 1 : 0);
    return 0;
  });

  const views = [
    { id: 'radios' as const, label: 'Radios', icon: 'ri-radio-line' },
    { id: 'categorias' as const, label: 'Categorías', icon: 'ri-stack-line' },
    { id: 'beneficios' as const, label: 'Beneficios', icon: 'ri-star-line' },
    { id: 'reglas' as const, label: 'Reglas', icon: 'ri-scales-line' },
  ];

  const sorts = [
    { id: 'ranking' as const, label: 'Ranking', icon: 'ri-trophy-line' },
    { id: 'oyentes' as const, label: 'Más Oyentes', icon: 'ri-headphone-line' },
    { id: 'rating' as const, label: 'Mejor Rating', icon: 'ri-star-line' },
    { id: 'tendencia' as const, label: 'Tendencia', icon: 'ri-fire-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative w-full h-[420px] md:h-[500px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20radio%20broadcast%20headquarters%20nighttime%20panoramic%20view%20with%20glowing%20amber%20and%20warm%20golden%20lights%2C%20multiple%20LED%20screens%20showing%20live%20audio%20metrics%20and%20world%20map%2C%20state-of-the-art%20broadcast%20control%20room%2C%20sleek%20modern%20architecture%20with%20glass%20walls%2C%20sophisticated%20professional%20atmosphere%2C%20dramatic%20editorial%20architectural%20photography%20with%20rich%20warm%20tones%20and%20elegant%20composition&width=1600&height=600&seq=rv-hero&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/30 to-foreground-950/70"></div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/20 text-amber-300 text-xs font-semibold border border-amber-500/30 mb-4 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
            RADIOS VERIFICADAS Y DESTACADAS
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-3 max-w-2xl">
            Las Mejores Radios de <span className="text-amber-400">RFW</span>
          </h1>
          <p className="text-white/70 max-w-xl text-sm md:text-base">
            Cinco categorías que reconocen la excelencia radial. Solo los Super Admins otorgan estas insignias.
          </p>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-6">
          {[
            { label: 'Radios', value: statsRadiosVerificadas.totalRadiosVerificadas.toString() },
            { label: 'Elite', value: statsRadiosVerificadas.porCategoria.elite.toString() },
            { label: 'Oficiales', value: statsRadiosVerificadas.porCategoria.oficial.toString() },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-xl md:text-2xl font-bold text-white">{s.value}</p>
              <p className="text-[10px] text-white/50">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Bar + Controls */}
      <section className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-xl border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-[10px]"></i>
            RFW
          </Link>

          <div className="flex items-center gap-4 text-[10px] text-foreground-400 ml-auto">
            <span className="flex items-center gap-1">
              <i className="ri-verified-badge-line text-emerald-600 text-[10px]"></i>
              {statsRadiosVerificadas.totalRadiosVerificadas} verificadas
            </span>
            <span className="hidden sm:flex items-center gap-1">
              <i className="ri-headphone-line text-rose-600 text-[10px]"></i>
              {formatearNumero(statsRadiosVerificadas.totalOyentes)} oyentes
            </span>
            <span className="hidden sm:flex items-center gap-1">
              <i className="ri-check-double-line text-sky-600 text-[10px]"></i>
              {statsRadiosVerificadas.tasaAprobacion}
            </span>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-3 flex flex-wrap items-center gap-2">
          <div className="inline-flex bg-background-100 rounded-full p-0.5 border border-background-200/70">
            {views.map((v) => (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  view === v.id ? 'bg-primary-500 text-white' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className={`${v.icon} text-[9px]`}></i>
                {v.label}
              </button>
            ))}
          </div>

          {view === 'radios' && (
            <>
              <div className="flex flex-wrap items-center gap-1">
                <button
                  onClick={() => setCategoriaFilter('todas')}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-all whitespace-nowrap cursor-pointer border ${
                    categoriaFilter === 'todas' ? 'bg-primary-500 text-white border-primary-500' : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  Todas
                </button>
                {categoriasVerificacion.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setCategoriaFilter(cat.id)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all whitespace-nowrap cursor-pointer border ${
                      categoriaFilter === cat.id ? `${cat.bg} ${cat.color} ${cat.borde}` : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                    }`}
                  >
                    <i className={`${cat.icono} text-[8px]`}></i>
                    {cat.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1 ml-auto">
                {sorts.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSortBy(s.id)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === s.id ? 'bg-rose-100 text-rose-700' : 'text-foreground-400 hover:text-foreground-600'
                    }`}
                  >
                    <i className={`${s.icon} text-[8px]`}></i>
                    {s.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {view === 'radios' && (
          <>
            {sorted.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-sm font-bold text-foreground-500 mb-1">Sin resultados</p>
                <p className="text-xs text-foreground-400 mb-4">No hay radios en esta categoría con los filtros actuales.</p>
                <button
                  onClick={() => { setCategoriaFilter('todas'); setSortBy('ranking'); }}
                  className="px-4 py-2 rounded-full bg-primary-500 text-white text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-all"
                >
                  Limpiar filtros
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {sorted.map(radio => (
                  <RadioVerificadaCard key={radio.id} radio={radio} />
                ))}
              </div>
            )}
          </>
        )}

        {view === 'categorias' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {categoriasVerificacion.map((cat) => {
                const count = statsRadiosVerificadas.porCategoria[cat.id];
                const radios = getRadiosPorCategoria(cat.id);
                return (
                  <div key={cat.id} className={`p-5 rounded-2xl border-2 ${cat.borde} bg-background-50 text-center relative overflow-hidden`}>
                    <div className={`absolute top-0 left-0 right-0 h-1 ${cat.bg.replace('bg-', 'bg-')}`}></div>
                    <div className={`w-16 h-16 rounded-full ${cat.bg} flex items-center justify-center mx-auto mb-3 mt-3`}>
                      <i className={`${cat.icono} text-3xl ${cat.color}`}></i>
                    </div>
                    <p className="text-lg font-extrabold text-foreground-950 mb-1">{cat.label}</p>
                    <p className="text-[10px] text-foreground-500 mb-3 leading-relaxed">{cat.descripcion}</p>
                    <span className={`inline-flex items-center gap-1 text-xs font-bold ${cat.color}`}>
                      <i className="ri-radio-line text-[10px]"></i>
                      {count} radios
                    </span>
                    <div className="flex -space-x-2 justify-center mt-3">
                      {radios.slice(0, 5).map(r => (
                        <img key={r.id} src={r.logoUrl} alt={r.nombre} className="w-9 h-9 rounded-full object-cover border-2 border-background-50" title={r.nombre} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {categoriasVerificacion.map((cat) => {
                const radios = getRadiosPorCategoria(cat.id);
                return (
                  <div key={cat.id} className={`p-4 rounded-xl bg-background-50 border ${cat.borde}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 rounded-full ${cat.bg} flex items-center justify-center`}>
                        <i className={`${cat.icono} text-sm ${cat.color}`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground-950">{cat.label}</p>
                        <p className="text-[10px] text-foreground-500">Prioridad: Nivel {cat.prioridad}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {radios.map((r, i) => (
                        <div key={r.id} className="flex items-center gap-2 text-[10px] py-1">
                          <span className="w-5 text-center font-bold text-foreground-400">{i + 1}</span>
                          <img src={r.logoUrl} alt="" className="w-5 h-5 rounded-full object-cover" />
                          <span className="text-foreground-700 font-medium truncate flex-1">{r.nombre}</span>
                          <span className="text-foreground-400">{r.frecuencia}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === 'beneficios' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
              {beneficiosVerificacion.map((beneficio) => (
                <div key={beneficio.id} className={`p-6 rounded-2xl ${beneficio.bgColor} border border-background-200/50 text-center`}>
                  <div className="w-14 h-14 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-3">
                    <i className={`${beneficio.icono} text-2xl ${beneficio.color}`}></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-950 mb-2">{beneficio.titulo}</p>
                  <p className="text-[11px] text-foreground-500 leading-relaxed">{beneficio.descripcion}</p>
                </div>
              ))}
            </div>

            <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
              <h3 className="text-sm font-bold text-foreground-950 mb-4 text-center">¿Dónde se ve la insignia?</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {ubicacionesInsignia.map((ubi) => (
                  <div key={ubi.id} className="p-3 rounded-xl bg-background-50 border border-background-200/50 text-center">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2">
                      <i className={`${ubi.icono} text-base text-emerald-600`}></i>
                    </div>
                    <p className="text-xs font-bold text-foreground-950">{ubi.nombre}</p>
                    <p className="text-[9px] text-foreground-400 mt-0.5">{ubi.descripcion}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {view === 'reglas' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {reglasRadiosVerificadas.map((regla) => (
                <div key={regla.id} className={`p-6 rounded-2xl bg-gradient-to-br ${regla.gradiente} bg-opacity-10 border ${regla.colorBorde} bg-background-50`}>
                  <div className="w-12 h-12 rounded-full bg-background-50 flex items-center justify-center mb-4">
                    <i className={`${regla.icono} text-xl text-foreground-950`}></i>
                  </div>
                  <p className="text-base font-bold text-foreground-950 mb-2">{regla.titulo}</p>
                  <p className="text-sm text-foreground-600 leading-relaxed">{regla.descripcion}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: 'Total Radios', value: statsRadiosVerificadas.totalRadiosVerificadas.toString(), icon: 'ri-radio-line', color: 'text-sky-600 bg-sky-50' },
                { label: 'Elite + Oficial', value: (statsRadiosVerificadas.porCategoria.elite + statsRadiosVerificadas.porCategoria.oficial).toString(), icon: 'ri-sparkling-2-line', color: 'text-violet-600 bg-violet-50' },
                { label: 'Premium', value: statsRadiosVerificadas.porCategoria.premium.toString(), icon: 'ri-vip-crown-line', color: 'text-rose-600 bg-rose-50' },
                { label: 'Oyentes', value: formatearNumero(statsRadiosVerificadas.totalOyentes), icon: 'ri-headphone-line', color: 'text-emerald-600 bg-emerald-50' },
                { label: 'Tasa Aprob.', value: statsRadiosVerificadas.tasaAprobacion, icon: 'ri-check-double-line', color: 'text-amber-600 bg-amber-50' },
              ].map((s, i) => (
                <div key={i} className={`p-4 rounded-xl ${s.color.replace('text-', 'bg-').replace('600', '50')} border border-background-200/70 text-center`}>
                  <div className={`w-8 h-8 rounded-full ${s.color} flex items-center justify-center mx-auto mb-2`}>
                    <i className={`${s.icon} text-sm`}></i>
                  </div>
                  <p className="text-lg font-bold text-foreground-950">{s.value}</p>
                  <p className="text-[10px] text-foreground-500">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="p-6 rounded-2xl bg-amber-50 border border-amber-200/50 text-center">
              <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
                <i className="ri-admin-line text-xl text-white"></i>
              </div>
              <p className="text-base font-bold text-foreground-950 mb-1">
                Solo SA puede otorgar verificaciones.
              </p>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                En RFW las insignias de Radio Verificada, Destacada, Premium, Elite y Oficial solo pueden ser otorgadas por los Super Admins. No se compran, no se regalan y no son automáticas. Cada insignia representa una evaluación rigurosa de calidad, trayectoria y comunidad.
              </p>
            </div>
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/explorar"
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-foreground-300 text-foreground-600 text-xs font-medium transition-all"
          >
            <i className="ri-compass-3-line"></i>
            Explorar Radios
          </Link>
          <Link
            to="/tv-app"
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-foreground-300 text-foreground-600 text-xs font-medium transition-all"
          >
            <i className="ri-tv-line"></i>
            Ver en TV App
          </Link>
          <Link
            to="/rankings"
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-foreground-300 text-foreground-600 text-xs font-medium transition-all"
          >
            <i className="ri-trophy-line"></i>
            Ver Rankings
          </Link>
        </div>
      </section>
    </div>
  );
}